import React, { useEffect, useState } from "react";
import { CLASSIFICATION_URL } from "../../helpers/variables";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import { Add, Clear, Visibility } from "@mui/icons-material";
import {
  Autocomplete,
  Backdrop,
  Badge,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import confirm from "../../Components/Confirm.tsx";

const Labels = () => {
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(false);

  const departments = [...new Set(labels.map((i) => i.dept))];
  const subDepartments = [...new Set(labels.map((i) => i.sub_dept))];

  useEffect(() => {
    getLabelList();
  }, []);

  async function getLabelList() {
    setLoading(true);
    let res = await fetch(`${CLASSIFICATION_URL}/labels_list`);
    let data = await res.json();
    setLabels(data.map((i) => ({ ...i, isNew: true })));
    setLoading(false);
  }

  async function updateLabel(labelID, updateLabel) {
    let userConfirmation = await confirm({
      message: `Are you sure you want to update this label`,
    });
    if (!userConfirmation) return false;

    setLoading(true);
    let res = await fetch(
      `${CLASSIFICATION_URL}/update_label?label_id=${labelID}`,
      {
        method: "PATCH",
        headers: {
          "Content-type": "application/json",
        },
        body: JSON.stringify(updateLabel),
      }
    );
    setLoading(false);
    if (res.status === 204) return true;
    return false;
  }

  const [aliasLabelDetails, setAliasLabelDetails] = useState({
    anchorEl: null,
    labelID: null,
    label: "",
    department: "",
    subDepartment: "",
  });

  async function addAliasLabel() {
    let { labelID, label, department, subDepartment } = aliasLabelDetails;
    let userConfirmation = await confirm({
      message: `Are you sure you want to add this alias label`,
    });
    if (!userConfirmation) return false;

    let newAliasLabel = {
      label,
      dept: department,
      sub_dept: subDepartment,
    };

    setLoading(true);

    await fetch(`${CLASSIFICATION_URL}/add_alias_label?label_id=${labelID}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(newAliasLabel),
    });

    setLabels((prev) =>
      prev.map((label) =>
        label._id === labelID
          ? {
              ...label,
              alias_labels: [...label.alias_labels, newAliasLabel],
            }
          : label
      )
    );

    handleAliasMenuClose();
    setLoading(false);
  }

  function handleAliasDialogClose() {
    setAliasLabelDetails({
      anchorEl: null,
      labelID: null,
      label: "",
      department: "",
      subDepartment: "",
    });
  }

  function handleAliasMenuClose() {
    setAliasLabelDetails((prev) => ({
      ...prev,
      anchorEl: null,
      label: "",
    }));
  }

  const targetLabel =
    labels.find((i) => i["_id"] === aliasLabelDetails.labelID) || {};
  const aliasLabels = targetLabel.alias_labels || [];

  const departmentList = [...new Set(labels.map((i) => i.dept))].sort((a, b) =>
    a.localeCompare(b)
  );

  const subDepartmentList = [...new Set(labels.map((i) => i.sub_dept))].sort(
    (a, b) => a.localeCompare(b)
  );

  function getSubDepartmentList(label) {
    return [
      ...new Set(
        labels.filter((i) => i["dept"] === label["dept"]).map((i) => i.sub_dept)
      ),
    ].sort((a, b) => a.localeCompare(b));
  }

  return (
    <>
      <DataGrid
        loading={loading}
        getRowId={(row) => row._id}
        rows={labels}
        showToolbar
        disableRowSelectionOnClick
        columns={[
          {
            field: "label",
            headerName: "Label",
            flex: 1,
          },
          {
            field: "dept",
            headerName: "Department",
            flex: 1,
            type: "singleSelect",
            valueOptions: departmentList,
          },
          {
            field: "sub_dept",
            headerName: "Sub Department",
            flex: 1,
            type: "singleSelect",
            valueOptions: subDepartmentList,
            display: "flex",
            renderCell: (params) => {
              return (
                <Autocomplete
                  fullWidth
                  size="small"
                  disablePortal
                  sx={{ alignItems: "center" }}
                  blurOnSelect
                  disableClearable
                  options={getSubDepartmentList(params.row)}
                  value={params.value}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Sub Department" />
                  )}
                  onChange={async (_, v) => {
                    let update = await updateLabel(params.id, { sub_dept: v });
                    if (!update) return;
                    setLabels((prev) =>
                      prev.map((item) =>
                        item._id === params.id ? { ...item, sub_dept: v } : item
                      )
                    );
                  }}
                />
              );
            },
          },
          {
            field: "actions",
            headerName: "Action",
            type: "actions",
            getActions: ({ id, row, columns }) => [
              <GridActionsCellItem
                icon={
                  <Tooltip title="Show Alias Label" arrow>
                    <Badge
                      badgeContent={row.alias_labels.length}
                      color="primary"
                    >
                      <Visibility />
                    </Badge>
                  </Tooltip>
                }
                onClick={() =>
                  setAliasLabelDetails((prev) => ({
                    ...prev,
                    labelID: id,
                    department: row.dept,
                    subDepartment: row.sub_dept,
                  }))
                }
              />,
            ],
          },
        ]}
      />

      <Dialog
        open={Boolean(aliasLabelDetails.labelID)}
        onClose={handleAliasDialogClose}
        maxWidth="lg"
        fullWidth
      >
        <Toolbar variant="dense">
          <Typography variant="h6" flexGrow={1}>
            Alias Labels of {targetLabel.label}
          </Typography>
          <Button
            variant="contained"
            size="small"
            onClick={(e) =>
              setAliasLabelDetails((prev) => ({
                ...prev,
                anchorEl: e.currentTarget,
              }))
            }
            sx={{ mr: 3 }}
          >
            Add Alias Label
          </Button>
          <IconButton onClick={handleAliasDialogClose}>
            <Clear />
          </IconButton>
        </Toolbar>
        <DialogContent sx={{ height: "100vh" }}>
          <Table
            size="small"
            stickyHeader
            sx={{ border: 1, borderColor: "divider" }}
          >
            <TableHead>
              <TableRow
                sx={{
                  "& .MuiTableCell-head": { backgroundColor: "lightgrey" },
                }}
              >
                <TableCell>Label</TableCell>
                <TableCell>Department</TableCell>
                <TableCell>Sub Department</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {aliasLabels.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>{item.label}</TableCell>
                  <TableCell>{item.dept}</TableCell>
                  <TableCell>{item.sub_dept}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <Backdrop open={loading}>
          <CircularProgress />
        </Backdrop>
      </Dialog>

      <Popover
        anchorEl={aliasLabelDetails.anchorEl}
        open={Boolean(aliasLabelDetails.anchorEl)}
        onClose={handleAliasMenuClose}
        disableRestoreFocus
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Stack
          gap={2}
          p={1}
          component={"form"}
          onSubmit={(e) => {
            e.preventDefault();
            addAliasLabel();
          }}
        >
          <TextField
            size="small"
            placeholder="Label"
            name="label"
            autoFocus
            value={aliasLabelDetails.label}
            onChange={(e) =>
              setAliasLabelDetails((prev) => ({
                ...prev,
                [e.target.name]: e.target.value,
              }))
            }
          />
          <TextField
            size="small"
            label="Department"
            name="department"
            value={aliasLabelDetails.department}
            onChange={(e) =>
              setAliasLabelDetails((prev) => ({
                ...prev,
                [e.target.name]: e.target.value,
              }))
            }
            select
          >
            {departments.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label="Sub Department"
            name="subDepartment"
            value={aliasLabelDetails.subDepartment}
            onChange={(e) =>
              setAliasLabelDetails((prev) => ({
                ...prev,
                [e.target.name]: e.target.value,
              }))
            }
            select
          >
            {subDepartments.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
          <Button variant="contained" size="small" type="submit">
            Submit
          </Button>
        </Stack>
      </Popover>
    </>
  );
};

export default Labels;
